import { FlexBox } from '@airship-platform/jam-web/components';
import { RightArrowIcon } from '@airship-platform/jam-web/icons';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link } from 'react-router';
import { SectionWrapper } from '../../patterns';
import ROUTE_PATHS from '../../routes/paths';
import ConfirmDeleteModal from './optionsForm/ConfirmDeleteModal';
import { OptionFormInputs } from './optionsForm/optionForm.models';
import OptionFormWrapper from './optionsForm/OptionFormWrapper';
import OptionNamesController from './optionsForm/OptionNamesController';
import { useOptionsUpsert } from './optionsForm/useOptionsUpsert';

const MANUAL_OPTION_FORM_ID = 'manual-option-form';
type OptionsUpsertPayload = ReturnType<
  ReturnType<typeof useOptionsUpsert>['buildUpsertPayload']
>;

const ManualOptionPage: React.FC = () => {
  const {
    isUpserting,
    currentOption,
    options,
    currentProposal,
    optionForm,
    upsertOptions,
    buildUpsertPayload,
  } = useOptionsUpsert();

  const [upsertPayload, setUpsertPayload] =
    useState<OptionsUpsertPayload | null>(null);

  if (!currentProposal) {
    return null;
  }

  const hasOptions = options.length > 0;

  const subHeader = hasOptions
    ? ''
    : 'Name your options, set the order, then start adding items.';

  const header = hasOptions ? 'Update options' : 'Build proposal';

  const leftLabel = hasOptions ? 'Back to options' : 'Back to details';
  const leftLink = hasOptions
    ? ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
        appointmentId: currentProposal.appointmentId,
        appointmentProposalId: currentProposal.appointmentProposalId,
        extraPath: currentOption?.appointmentProposalOptionId,
      })
    : ROUTE_PATHS.SCHEDULE_DETAIL(currentProposal.appointmentId);

  const rightLabel = hasOptions ? 'Update' : 'Add items';
  const rightColour =
    !hasOptions || optionForm.formState.isDirty ? 'primary' : 'softGold';

  const renderExtraLinks = (options: {
    title: string;
    description: string;
    buttonLabel: string;
    buttonLink: string;
  }) => (
    <FlexBox
      alignItems={['flex-start', 'center']}
      flexDirection={['column', 'row']}
      gap={2}
    >
      <FlexBox flex={1} flexDirection={'column'} gap={0.5}>
        <Typography variant="subtitle2">{options.title}</Typography>
        <Typography variant="body2">{options.description}</Typography>
      </FlexBox>
      <FlexBox>
        <Button
          component={Link}
          to={options.buttonLink}
          color="softGold"
          sx={{ width: 140 }}
        >
          {options.buttonLabel}
        </Button>
      </FlexBox>
    </FlexBox>
  );

  const onSubmit = (data: OptionFormInputs) => {
    const payload = buildUpsertPayload(data);

    if (payload.deletedOptionsWithItems.length) {
      setUpsertPayload(payload);
      return;
    }

    return upsertOptions(payload);
  };

  const onConfirmDelete = (confirmed: boolean) => {
    if (confirmed && upsertPayload) {
      upsertOptions(upsertPayload);
    } else {
      optionForm.reset();
    }
    setUpsertPayload(null);
  };

  return (
    <SectionWrapper
      maxWidth={'sm'}
      header={header}
      subheader={subHeader}
      actionsButtons={{
        left: {
          label: leftLabel,
          component: Link,
          to: leftLink,
        },
        right: {
          form: MANUAL_OPTION_FORM_ID,
          label: rightLabel,
          type: 'submit',
          color: rightColour,
          disabled: isUpserting,
          endIcon: <RightArrowIcon />,
        },
      }}
    >
      <OptionFormWrapper
        id={MANUAL_OPTION_FORM_ID}
        onSubmit={optionForm.handleSubmit(onSubmit)}
      >
        <OptionNamesController />

        {!hasOptions && (
          <>
            <Divider flexItem />

            {renderExtraLinks({
              title: 'Apply a template',
              description:
                'Prefer not to start from scratch? Browse your proposal and option templates, or pick from previous proposals.',
              buttonLabel: 'Apply templates',
              buttonLink: 'templates',
            })}
          </>
        )}
      </OptionFormWrapper>

      <ConfirmDeleteModal
        optionsNames={
          upsertPayload?.toUpdate
            .filter((d) => d.status === 'DELETED')
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((d) => d.name) || []
        }
        isOpen={!!upsertPayload}
        onClose={onConfirmDelete}
      />
    </SectionWrapper>
  );
};

export default ManualOptionPage;
